import React, { useRef, useState, useCallback, useEffect } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { Tooltip } from "./Tooltip";
import T from "./Translate/T";
import XIcon from "./icons/XIcon";
import { useTranslate } from "../context/TranslateContext";

// 5 Concorde Place, North York, ON M3C 3M8 coordinates
const MAP_LATITUDE = 43.73007466177665;
const MAP_LONGITUDE = -79.32740447481187;

// API endpoint for contact form
const CONTACT_API_URL = "https://api.ramirez-carlos.com/contact";

// ============================================================================
// VALIDATION RULES - Returns error keys for translation
// ============================================================================
const validators = {
  name: (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Name is required";
    }
    if (trimmed.length < 2) {
      return "Name must be at least 2 characters";
    }
    if (trimmed.length > 50) {
      return "Name must be less than 50 characters";
    }
    // Allow letters, spaces, hyphens, apostrophes (for names like O'Connor, Mary-Jane)
    if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmed)) {
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return null;
  },

  email: (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Email is required";
    }
    // Comprehensive email regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      return "Please enter a valid email address";
    }
    if (trimmed.length > 100) {
      return "Email must be less than 100 characters";
    }
    return null;
  },

  message: (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Message is required";
    }
    if (trimmed.length < 10) {
      return "Message must be at least 10 characters";
    }
    if (trimmed.length > 2000) {
      return "Message must be less than 2000 characters";
    }
    return null;
  },
};

// Validate all fields
const validateForm = (formData) => {
  const errors = {};
  
  const nameError = validators.name(formData.name);
  if (nameError) errors.name = nameError;
  
  const emailError = validators.email(formData.email);
  if (emailError) errors.email = emailError;
  
  const messageError = validators.message(formData.message);
  if (messageError) errors.message = messageError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Custom Alert Component with Translation
const Alert = ({ type, message, onClose }) => {
  const styles = {
    container: {
      padding: "16px 20px",
      borderRadius: "8px",
      marginBottom: "20px",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      animation: "slideIn 0.3s ease-out",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      ...(type === "success"
        ? {
            backgroundColor: "#d4edda",
            border: "1px solid #28a745",
            color: "#155724",
          }
        : {
            backgroundColor: "#f8d7da",
            border: "1px solid #dc3545",
            color: "#721c24",
          }),
    },
    icon: {
      fontSize: "24px",
      flexShrink: 0,
    },
    content: {
      flex: 1,
    },
    title: {
      fontWeight: "600",
      fontSize: "16px",
      marginBottom: "4px",
    },
    message: {
      fontSize: "14px",
      margin: 0,
    },
    closeBtn: {
      background: "none",
      border: "none",
      fontSize: "20px",
      cursor: "pointer",
      padding: "0",
      lineHeight: 1,
      opacity: 0.7,
      color: "inherit",
    },
  };

  return (
    <div style={styles.container}>
      <span style={styles.icon}>
        {type === "success" ? "✓" : "✕"}
      </span>
      <div style={styles.content}>
        <div style={styles.title}>
          <T>{type === "success" ? "Success!" : "Error"}</T>
        </div>
        <p style={styles.message}><T>{message}</T></p>
      </div>
      <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );
};

// Inline Error Component with Translation
const FieldError = ({ error }) => {
  if (!error) return null;
  
  return (
    <div
      style={{
        color: "#dc3545",
        fontSize: "13px",
        marginTop: "6px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <span style={{ fontSize: "14px" }}>⚠</span>
      <T>{error}</T>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Contact = () => {
  const form = useRef();
  const [sendingMail, setSendingMail] = useState(false);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    website_url: "", // Honeypot field
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { currentLang, translateBatch, isEnglish } = useTranslate();
  
  // Translated placeholders
  const [placeholders, setPlaceholders] = useState({
    name: "John Doe",
    email: "john@example.com",
    message: "Tell me about your project...",
  });

  // Translate placeholders when language changes
  useEffect(() => {
    const translatePlaceholders = async () => {
      if (isEnglish) {
        setPlaceholders({
          name: "John Doe",
          email: "john@example.com",
          message: "Tell me about your project...",
        });
        return;
      }

      try {
        const results = await translateBatch(
          ["John Doe", "john@example.com", "Tell me about your project..."],
          currentLang
        );
        setPlaceholders({
          name: results[0] || "John Doe",
          email: results[1] || "john@example.com",
          message: results[2] || "Tell me about your project...",
        });
      } catch (error) {
        console.error("Placeholder translation error:", error);
      }
    };

    translatePlaceholders();
  }, [currentLang, isEnglish]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing (if field was touched)
    if (touched[name] && errors[name]) {
      const validator = validators[name];
      if (validator) {
        const error = validator(value);
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate on blur
    const validator = validators[name];
    if (validator) {
      const error = validator(value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 6000);
  };

  const sendEmail = useCallback(async (e) => {
    e.preventDefault();
    setAlert(null);
    
    // Validate all fields
    const validation = validateForm(formData);
    setErrors(validation.errors);
    setTouched({ name: true, email: true, message: true });
    
    if (!validation.isValid) {
      showAlert("error", "Please fix the errors below before submitting.");
      return;
    }
    
    if (!executeRecaptcha) {
      showAlert("error", "reCAPTCHA not loaded. Please refresh the page.");
      return;
    }

    setSendingMail(true);

    try {
      const recaptchaToken = await executeRecaptcha("contact_form");

      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
          website_url: formData.website_url,
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFormData({ name: "", email: "", message: "", website_url: "" });
        setErrors({});
        setTouched({});
        form.current.reset();
        showAlert("success", "Message sent successfully! I'll get back to you soon.");
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      showAlert("error", error.message || "Something went wrong. Please try again.");
    } finally {
      setSendingMail(false);
    }
  }, [executeRecaptcha, formData]);

  // Get input style based on error state
  const getInputStyle = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return {
        borderColor: "#dc3545",
        boxShadow: "0 0 0 2px rgba(220, 53, 69, 0.15)",
      };
    }
    if (touched[fieldName] && !errors[fieldName] && formData[fieldName]) {
      return {
        borderColor: "#28a745",
        boxShadow: "0 0 0 2px rgba(40, 167, 69, 0.15)",
      };
    }
    return {};
  };

  return (
    <section id="contact" className="section bg-primary">
      <div className="container">
        <div className="row">
          <div className="col-lg-5 text-center text-lg-start wow fadeInUp">
            <h2 className="text-10 fw-600 mb-5">
              <T>Let's get in touch</T>
            </h2>
            <p className="text-5 mb-4">
              <T>I enjoy discussing new projects and design challenges.</T>{" "}
              <T>Please share as much info as possible so we can get the most out of our first catch-up.</T>
            </p>

            {/* Map Section */}
            <div className="mb-4" style={{ borderRadius: "8px", overflow: "hidden" }}>
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${MAP_LATITUDE},${MAP_LONGITUDE}&t=m&z=15&output=embed&iwloc=near`}
                style={{
                  width: "100%",
                  height: "200px",
                  border: "0",
                  borderRadius: "8px",
                }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <h3 className="text-5 fw-600"><T>Living In:</T></h3>
            <address className="text-4">
              Toronto, Ontario, Canada.
            </address> 
            <h3 className="text-5 fw-600"><T>Call:</T></h3>
            <p className="text-4">(+1) 647 771 158*</p>
            <ul className="social-icons social-icons-lg justify-content-center justify-content-lg-start mt-5">
              <li className="social-icons-twitter">
                <Tooltip text="X (Twitter)" placement="top">
                  <a
                    href="https://x.com/eramirezcarlos/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <XIcon size={20} />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-linkedin">
                <Tooltip text="LinkedIn" placement="top">
                  <a
                    href="https://www.linkedin.com/in/carlos-a-ramirez-2800281a8/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-linkedin" />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-github">
                <Tooltip text="Github" placement="top">
                  <a
                    href="https://github.com/eramirezcarlos"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-github" />
                  </a>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div
            className="col-lg-6 ms-auto mt-5 mt-lg-0 wow fadeInUp"
            data-wow-delay="0.3s"
          >
            <h2 className="text-10 fw-600 text-center text-lg-start mb-5">
              <T>Estimate your Project?</T>
            </h2>

            {/* Alert Messages */}
            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
              />
            )}

            {/* Contact Form */}
            <form
              id="contact-form"
              className="form-border"
              method="post"
              ref={form}
              onSubmit={sendEmail}
              noValidate
            >
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label" htmlFor="name">
                    <T>What is Your Name:</T>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className="form-control py-1"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={getInputStyle("name")}
                    maxLength={50}
                    placeholder={placeholders.name}
                  />
                  <FieldError error={touched.name && errors.name} />
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="email">
                    <T>Your Email Address:</T>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="form-control py-1"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={getInputStyle("email")}
                    maxLength={100}
                    placeholder={placeholders.email}
                  />
                  <FieldError error={touched.email && errors.email} />
                </div>
                <div className="col-12">
                  <label className="form-label" htmlFor="message">
                    <T>How can I Help you?:</T>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-control py-1"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={getInputStyle("message")}
                    maxLength={2000}
                    placeholder={placeholders.message}
                  />
                  <FieldError error={touched.message && errors.message} />
                  <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px", textAlign: "right" }}>
                    {formData.message.length}/2000
                  </div>
                </div>
                
                {/* Honeypot field - hidden from users, bots will fill it */}
                <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 text-center text-lg-start">
                  <button
                    id="submit-btn"
                    className="btn btn-dark rounded-0"
                    type="submit"
                    disabled={sendingMail}
                  >
                    {sendingMail ? (
                      <>
                        <span
                          role="status"
                          aria-hidden="true"
                          className="spinner-border spinner-border-sm align-self-center me-2"
                        ></span>
                        <T>Sending...</T>
                      </>
                    ) : (
                      <>
                        <T>Send</T>{" "}
                        <span className="ms-3">
                          <i className="fas fa-arrow-right" />
                        </span>
                      </>
                    )}
                  </button>
                </div>
                <div className="col-12">
                  <p className="text-muted small mb-0">
                    <T>This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.</T>
                  </p>
                </div>
              </div>
            </form>
            {/* Contact Form end */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;